/**
 * Polygon geometry calculations
 * Area, centroid, bounds, containment checks, simplification
 *
 * Extracted from GeoTiffViewerEmbedded.tsx to provide reusable
 * polygon operations across the application.
 */

import { PolygonCoordinates, Bounds } from './types';
import { METERS_PER_DEGREE_LAT, getMetersPerDegreeLng } from './coordinateTransforms';

/**
 * Calculate the area of a polygon in square meters
 * Uses the Shoelace formula with proper coordinate conversion
 *
 * @param polygon - Array of [lat, lng] coordinates
 * @returns Area in square meters
 */
export function calculatePolygonArea(polygon: PolygonCoordinates): number {
  if (polygon.length < 3) return 0;

  // Calculate center latitude for accurate conversion
  const centerLat = polygon.reduce((sum, [lat]) => sum + lat, 0) / polygon.length;

  // Conversion factors at this latitude
  const latToMeters = METERS_PER_DEGREE_LAT;
  const lngToMeters = getMetersPerDegreeLng(centerLat);

  // Convert all coordinates to meters relative to first point
  const firstPoint = polygon[0];
  const pointsInMeters = polygon.map(([lat, lng]) => {
    const x = (lng - firstPoint[1]) * lngToMeters;
    const y = (lat - firstPoint[0]) * latToMeters;
    return [x, y];
  });

  // Apply Shoelace formula on meter coordinates
  let area = 0;
  for (let i = 0; i < pointsInMeters.length; i++) {
    const j = (i + 1) % pointsInMeters.length;
    area += pointsInMeters[i][0] * pointsInMeters[j][1];
    area -= pointsInMeters[j][0] * pointsInMeters[i][1];
  }

  return Math.abs(area / 2);
}

/**
 * Calculate the centroid (center point) of a polygon
 * Simple average of all vertices - works well for convex polygons
 *
 * @param polygon - Array of [lat, lng] coordinates
 * @returns Centroid as [lat, lng]
 */
export function calculatePolygonCentroid(polygon: PolygonCoordinates): [number, number] {
  if (polygon.length === 0) return [0, 0];

  const sumLat = polygon.reduce((sum, [lat]) => sum + lat, 0);
  const sumLng = polygon.reduce((sum, [, lng]) => sum + lng, 0);

  return [sumLat / polygon.length, sumLng / polygon.length];
}

/**
 * Check if a point is inside a polygon using ray-casting algorithm
 *
 * @param point - Point to test as [lat, lng]
 * @param polygon - Polygon as array of [lat, lng] coordinates
 * @returns True if point is inside polygon
 */
export function isPointInPolygon(
  point: [number, number],
  polygon: PolygonCoordinates
): boolean {
  const [lat, lng] = point;
  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [lat1, lng1] = polygon[i];
    const [lat2, lng2] = polygon[j];

    const intersect =
      lng1 > lng !== lng2 > lng &&
      lat < ((lat2 - lat1) * (lng - lng1)) / (lng2 - lng1) + lat1;

    if (intersect) inside = !inside;
  }

  return inside;
}

/**
 * Calculate the bounding box of a polygon
 *
 * @param polygon - Array of [lat, lng] coordinates
 * @returns Bounds object with north, south, east, west
 */
export function calculatePolygonBounds(polygon: PolygonCoordinates): Bounds {
  if (polygon.length === 0) {
    return { north: 0, south: 0, east: 0, west: 0 };
  }

  const lats = polygon.map(([lat]) => lat);
  const lngs = polygon.map(([, lng]) => lng);

  return {
    north: Math.max(...lats),
    south: Math.min(...lats),
    east: Math.max(...lngs),
    west: Math.min(...lngs),
  };
}

/**
 * Check if two polygons overlap
 * Uses bounding box check first, then detailed vertex checks
 *
 * @param poly1 - First polygon
 * @param poly2 - Second polygon
 * @returns True if polygons overlap
 */
export function doPolygonsOverlap(
  poly1: PolygonCoordinates,
  poly2: PolygonCoordinates
): boolean {
  // Quick bounding box check first
  const bounds1 = calculatePolygonBounds(poly1);
  const bounds2 = calculatePolygonBounds(poly2);

  const boundsOverlap = !(
    bounds1.east < bounds2.west ||
    bounds1.west > bounds2.east ||
    bounds1.north < bounds2.south ||
    bounds1.south > bounds2.north
  );

  if (!boundsOverlap) return false;

  // Detailed check: any vertex of one polygon inside the other
  for (const point of poly1) {
    if (isPointInPolygon(point, poly2)) return true;
  }

  for (const point of poly2) {
    if (isPointInPolygon(point, poly1)) return true;
  }

  return false;
}

/**
 * Calculate the perimeter of a polygon in meters
 *
 * @param polygon - Array of [lat, lng] coordinates
 * @returns Perimeter in meters
 */
export function calculatePolygonPerimeter(polygon: PolygonCoordinates): number {
  if (polygon.length < 2) return 0;

  const centerLat = polygon.reduce((sum, [lat]) => sum + lat, 0) / polygon.length;
  const latToMeters = METERS_PER_DEGREE_LAT;
  const lngToMeters = getMetersPerDegreeLng(centerLat);

  let perimeter = 0;

  for (let i = 0; i < polygon.length - 1; i++) {
    const [lat1, lng1] = polygon[i];
    const [lat2, lng2] = polygon[i + 1];

    const dx = (lng2 - lng1) * lngToMeters;
    const dy = (lat2 - lat1) * latToMeters;

    perimeter += Math.sqrt(dx * dx + dy * dy);
  }

  return perimeter;
}

/**
 * Simplify a polygon using Douglas-Peucker algorithm
 * Reduces number of vertices while maintaining shape
 *
 * @param polygon - Original polygon
 * @param toleranceMeters - Simplification tolerance (smaller = more detail)
 * @returns Simplified polygon
 */
export function simplifyPolygon(
  polygon: PolygonCoordinates,
  toleranceMeters: number = 0.5
): PolygonCoordinates {
  if (polygon.length <= 3) return polygon;

  // Convert tolerance to degrees (approximate)
  const centerLat = polygon.reduce((sum, [lat]) => sum + lat, 0) / polygon.length;
  const toleranceDeg = toleranceMeters / METERS_PER_DEGREE_LAT;

  return douglasPeucker(polygon, toleranceDeg);
}

/**
 * Douglas-Peucker algorithm implementation
 * Recursively simplifies polygon by removing points
 */
function douglasPeucker(
  points: PolygonCoordinates,
  tolerance: number
): PolygonCoordinates {
  if (points.length <= 2) return points;

  // Find point with maximum distance from line segment
  let maxDistance = 0;
  let maxIndex = 0;

  const start = points[0];
  const end = points[points.length - 1];

  for (let i = 1; i < points.length - 1; i++) {
    const distance = perpendicularDistance(points[i], start, end);
    if (distance > maxDistance) {
      maxDistance = distance;
      maxIndex = i;
    }
  }

  // If max distance is greater than tolerance, recursively simplify
  if (maxDistance > tolerance) {
    const left = douglasPeucker(points.slice(0, maxIndex + 1), tolerance);
    const right = douglasPeucker(points.slice(maxIndex), tolerance);

    return [...left.slice(0, -1), ...right];
  } else {
    return [start, end];
  }
}

/**
 * Calculate perpendicular distance from point to line segment
 */
function perpendicularDistance(
  point: [number, number],
  lineStart: [number, number],
  lineEnd: [number, number]
): number {
  const [px, py] = point;
  const [x1, y1] = lineStart;
  const [x2, y2] = lineEnd;

  const dx = x2 - x1;
  const dy = y2 - y1;

  if (dx === 0 && dy === 0) {
    // Line segment is a point
    return Math.sqrt((px - x1) ** 2 + (py - y1) ** 2);
  }

  const t = ((px - x1) * dx + (py - y1) * dy) / (dx * dx + dy * dy);

  if (t < 0) {
    return Math.sqrt((px - x1) ** 2 + (py - y1) ** 2);
  } else if (t > 1) {
    return Math.sqrt((px - x2) ** 2 + (py - y2) ** 2);
  }

  const projX = x1 + t * dx;
  const projY = y1 + t * dy;

  return Math.sqrt((px - projX) ** 2 + (py - projY) ** 2);
}

/**
 * Close a polygon (ensure last point equals first point)
 *
 * @param polygon - Polygon that may or may not be closed
 * @returns Closed polygon
 */
export function closePolygon(polygon: PolygonCoordinates): PolygonCoordinates {
  if (polygon.length === 0) return polygon;

  const first = polygon[0];
  const last = polygon[polygon.length - 1];

  // Already closed
  if (first[0] === last[0] && first[1] === last[1]) {
    return polygon;
  }

  return [...polygon, first];
}

/**
 * Remove closing point from polygon if present
 *
 * @param polygon - Potentially closed polygon
 * @returns Open polygon
 */
export function openPolygon(polygon: PolygonCoordinates): PolygonCoordinates {
  if (polygon.length < 2) return polygon;

  const first = polygon[0];
  const last = polygon[polygon.length - 1];

  if (first[0] === last[0] && first[1] === last[1]) {
    return polygon.slice(0, -1);
  }

  return polygon;
}
